import { redirect } from '@hub/router/domain-routes';
import type { AuthenticatedGuard } from '@hub/router/pages/_authenticated.guard';
import { publicHref } from '@hub/router/publicHref';
import { authUser } from '@hub/router/routerAuth';
import { HomePaths } from '../home/route.paths';
import { LoginPaths } from './route.paths';

// `mrf` (missing required fields) is the users-service flag meaning the account still needs the
// completion step. Single source so both guards below agree.
function needsAccountCompletion(flags: readonly string[] | null | undefined): boolean {
  return flags?.includes('mrf') ?? false;
}

// Completion page itself would loop; linkApple stays reachable while incomplete (apple.md); the
// entry routes resolve their own `?to=` first, so the step captures the deep-link, not the entry URL.
const ALLOWED_PATHS: Set<string> = new Set([
  LoginPaths.completeRegistration.path,
  LoginPaths.linkApple.path,
  LoginPaths.redirect.path,
  LoginPaths.register.path,
  LoginPaths.registerAccount.path,
  LoginPaths.registerProfile.path,
]);

// Force accounts flagged for completion onto the completion page, from public routes too: those are
// reachable while signed in, so gating only `_authenticated` let any deep-link skip the step.
// Carries the interrupted location as `to`, so finishing the step returns there instead of home.
export const accountCompletionGuard: AuthenticatedGuard = ({ context, location }) => {
  if (needsAccountCompletion(authUser(context.auth)?.flags) && !ALLOWED_PATHS.has(location.pathname)) {
    throw redirect({ to: LoginPaths.completeRegistration.path, search: { to: publicHref(location.href) }, replace: true });
  }
};

// Inverse, for the completion route's own beforeLoad: an account that no longer needs completion
// (already complete, or reached via back/deep-link) is sent home.
export const redirectCompletedAccountHome: AuthenticatedGuard = ({ context }) => {
  if (!needsAccountCompletion(authUser(context.auth)?.flags)) {
    throw redirect({ to: HomePaths.home.path, replace: true });
  }
};
